<template>
  <v-dialog
    v-model="showDialog"
    max-width="700"
    :persistent="inputChanged"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-show="isVisible"
        :color="color"
        :class="buttonClasses"
        :loading="loading"
        :large="large"
        :disabled="disabled"
        icon
        v-bind="attrs"
        v-on="on"
      >
        <v-icon v-if="iconLeft || (!iconRight && !!icon)">
          {{ icon }}
        </v-icon>
        {{ text }}
        <v-icon
          v-if="iconRight"
          right
        >
          {{ icon }}
        </v-icon>
      </v-btn>
    </template>
    <v-card
      v-if="!loading"
      :loading="submitting"
    >
      <v-card-title>
        <span class="text-h5">
          Neuen Externen Benutzer anlegen
        </span>
      </v-card-title>
      <v-card-text>
        <v-form
          ref="form"
          v-model="formValid"
          :disabled="submitting"
        >
          <v-container>
            <v-alert
              v-if="submitErrorMessage !== null"
              dismissible
              outlined
              type="warning"
              @input="submitErrorMessage = null"
            >
              <span>{{ submitErrorMessage }}</span>
            </v-alert>
            <v-alert
              v-if="submitSuccessMessage !== null"
              dismissible
              outlined
              type="info"
              @input="submitSuccessMessage = null"
            >
              <span>{{ submitSuccessMessage }}</span>
            </v-alert>
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="newUserDto.username"
                  :rules="usernameRules"
                  label="Benutzername"
                  required
                  @change="lowercaseUsername"
                ></v-text-field>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="newUserDto.password"
                  :rules="newUserDto.generateRegistrationLink ? [] : passwordRules"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPassword ? 'text' : 'password'"
                  :disabled="newUserDto.generateRegistrationLink"
                  autocomplete="change-password password"
                  label="Passwort"
                  counter
                  required
                  @click:append="showPassword = !showPassword"
                ></v-text-field>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="newUserDto.mail"
                  :rules="emailRules"
                  label="E-mail"
                  required
                ></v-text-field>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-autocomplete
                  v-if="realm && companies"
                  ref="companySelector"
                  v-model="newUserDto.companyId"
                  :rules="companyRules"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  label="Firma"
                  required
                  @change="newUserDto.company = companies.find(f => f.id === $event).name"
                ></v-autocomplete>
                <v-text-field
                  v-else
                  ref="companyName"
                  v-model="newUserDto.company"
                  :rules="companyRules"
                  label="Firma"
                ></v-text-field>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="newUserDto.name"
                  :rules="nameRules"
                  label="Vorname"
                  required
                ></v-text-field>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="newUserDto.lastName"
                  :rules="nameRules"
                  label="Nachname"
                  required
                ></v-text-field>
              </v-col>

              <v-col
                v-show="showGroupSelect"
                cols="12"
                md="6"
              >
                <v-text-field
                  :value="newUserDto.userGroups.join()"
                  label="Gruppen (read-only)"
                  readonly
                />
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="!formValid"
          :loading="submitting"
          depressed
          text
          color="warning"
          @click="registerUser"
        >
          Benutzer anlegen
        </v-btn>
        <v-btn
          color="primary"
          text
          :disabled="submitting"
          @click="showDialog = false"
        >
          Schließen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mdiTagCheck } from '@mdi/js';
import { adminGroups } from '@/main/routes.js';
import ExternalUser from '@/main/ExternalUser.js';

import NotificationObject from '@/main/NotificationObject.js';

export default {
  name: 'AddExternalUserButton',
  props: {
    companies: { type: Array, default: null },
    realm: { type: Number, default: null },
    text: { type: String, default: 'Neuen Benutzer anlegen' },
    color: { type: String, default: 'green' },
    buttonClasses: { type: String, default: 'white--text' },
    iconLeft: { type: Boolean, default: false },
    iconRight: { type: Boolean, default: false },
    icon: { type: String, default: mdiTagCheck },
    large: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    allowedUserRoles: { type: Array, default: () => [] },
    showGroupSelect: { type: Boolean, default: false }
  },
  data: () => ({
    showDialog: false,
    loading: false,
    submitting: false,
    submitSuccess: false,
    formValid: null,
    showPassword: false,
    submitErrorMessage: null,
    submitSuccessMessage: null,
    newUserDto: {
      username: '',
      name: '',
      lastName: '',
      company: '',
      companyId: '',
      mail: '',
      password: '',
      generateRegistrationLink: false,
      userGroups: ['LgSpotNU']
    },
    nameRules: [
      (v) => !!v || 'Name ist ein Pflichtfeld',
      (v) => v.length >= 3 || 'Name ist ein Pflichtfeld'
    ],
    usernameRules: [
      (v) => !!v || 'Benutzername ist ein Pflichtfeld',
      (v) =>
        /^[a-zA-Z]/.test(v) ||
        'Der Benutzername muss mit einem Buchstaben anfangen',
      (v) =>
        /^[a-zA-Z0-9]+$/.test(v) ||
        'Der Benutzername darf nur aus Buchstaben und Zahlen bestehen',
      (v) =>
        v.length >= 5 || 'Der Benutzername muss mindestens 5 Zeichen lang sein'
    ],
    companyRules: [
      (v) => !!v || 'Firma ist ein Pflichtfeld',
      (v) =>
        v.length >= 5 || 'Der Firmenname muss mindestens 5 Zeichen lang sein'
    ],
    emailRules: [
      (v) => !!v || 'E-Mailist ein Pflichtfeld',
      (v) => /.+@.+\..{2,}/.test(v) || 'E-Mail ist nicht valide'
    ],
    passwordRules: ExternalUser.PASSWORD_RULES
  }),
  computed: {
    isAdmin() {
      return this.$auth
        .user()
        .userRoles.some((role) => adminGroups.includes(role));
    },
    isUserRoleAllowed() {
      return this.$auth
        .user()
        .userRoles.some((role) => this.allowedUserRoles.includes(role));
    },
    isVisible() {
      return this.isAdmin || this.isUserRoleAllowed;
    },
    inputChanged() {
      return (
        Object.values(this.newUserDto).filter(
          (v) => typeof v === 'string' && v !== ''
        ).length > 0
      );
    }
  },
  async mounted() {
    if (this.realm !== null && this.companies === null) {
      throw new Error(
        `[${this.$options.name}] property companies is required when passing a realm as a property`
      );
    }
    this.loading = false;
  },
  methods: {
    registerUser: function () {
      if (!this.formValid) return;
      this.submitting = true;
      this.submitErrorMessage = null;
      this.submitSuccessMessage = null;

      const registerDto = this.newUserDto;
      if (registerDto.generateRegistrationLink) {
        registerDto.password = null;
      }

      ExternalUser.register(registerDto, this.realm)
        .then(({ status }) => {
          if (status === 201) {
            this.submitSuccessMessage = `Der Benutzer ${registerDto.username} (${registerDto.mail}) wurde erfolgreich angelegt.`;
            this.newUserDto = {
              username: '',
              name: '',
              lastName: '',
              company: '',
              companyId: '',
              mail: '',
              password: '',
              generateRegistrationLink: false,
              userGroups: ['LgSpotNU']
            };
            this.$refs.form.resetValidation();
            this.$emit('userAdded');
          } else {
            this.submitErrorMessage = 'Beim anlegen ist ein Fehler aufgetreten';
          }
        })
        .catch((e) => {
          if (e.response && e.response.data) {
            this.submitErrorMessage = 'Beim anlegen ist ein Fehler aufgetreten';
          }
          this.showMessage(['Fehler beim Registrieren', e.message], 'error');
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    lowercaseUsername: function () {
      this.newUserDto.username = this.newUserDto.username.toLowerCase();
    },
    showMessage(message, type = 'success') {
      this.$store.commit(
        'addNotification',
        new NotificationObject(type, message)
      );
    }
  }
};
</script>
